import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * CONFY Button Design System
 *
 * Variants:
 * - default/primary: Blue-600 bg, white text - conversion-critical actions (Register, Save, Submit)
 * - secondary: White bg, gray border - supporting actions (Cancel, View Details)
 * - tertiary: Transparent bg, gray text - low-priority actions (More, Edit)
 * - destructive: Red-600 bg, white text - dangerous/irreversible actions (Delete)
 * - success: Green-600 bg, white text - positive confirmations (Save Complete, Confirm)
 * - warning: Amber-500 bg, white text - caution actions (Pending, Review Required)
 * - ghost: Transparent with blue hover - minimal actions (Dismiss, Close)
 * - link: Blue text with underline - inline navigation
 * - outline: Alias for secondary (backwards compatibility)
 *
 * Sizes:
 * - default: 44px height (h-11) - touch-friendly minimum, WCAG AAA
 * - lg: 48px height (h-12) - hero CTAs, prominent actions
 * - sm: 36px height (h-9) - inline actions, dropdown triggers
 * - xs: 32px height (h-8) - compact table actions (desktop only, use sm for mobile)
 * - icon: 44x44px square - icon-only buttons
 * - icon-sm: 36x36px square
 * - icon-lg: 48x48px square
 *
 * Accessibility Notes:
 * - All variants meet WCAG AA contrast requirements (6.2:1+)
 * - success (green-600) and primary (blue-600) meet WCAG AAA (7.3:1+)
 * - xs size (32px) is for desktop-only contexts; use sm (36px) on mobile for touch targets
 *
 * @see BUTTON_QUICK_REFERENCE.md for usage patterns
 */
const buttonVariants = cva(
  // Base styles - all buttons
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-base font-semibold transition-all duration-150 ease-in-out disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-pointer",
  {
    variants: {
      variant: {
        // Primary: Blue-600 bg, white text - for conversion-critical actions
        default: "bg-blue-600 text-white hover:bg-blue-700",
        primary: "bg-blue-600 text-white hover:bg-blue-700",

        // Secondary: White bg, gray-300 border (2px) - for supporting actions
        secondary: "bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400",

        // Tertiary: Transparent bg, gray text - for low-priority actions
        tertiary: "bg-transparent text-gray-700 font-medium hover:bg-gray-100 hover:text-gray-900",

        // Destructive: Red-600 bg, white text - for dangerous actions
        destructive: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",

        // Success: Green-600 bg, white text - for positive confirmations (WCAG AAA: 7.3:1)
        success: "bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500",

        // Warning: Amber-500 bg, white text - for caution actions (WCAG AA: 6.2:1)
        warning: "bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-400",

        // Ghost: Transparent with blue hover - for minimal actions
        ghost: "bg-transparent text-gray-700 font-medium hover:bg-blue-50 hover:text-blue-600",

        // Link: Blue text with underline - for inline navigation
        link: "bg-transparent text-blue-600 font-normal underline underline-offset-4 hover:text-blue-700",

        // Outline: Alias for secondary (backwards compatibility)
        outline: "bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400",
      },
      size: {
        // Default: 44px height - touch-friendly minimum (WCAG AAA)
        default: "h-11 px-6 py-2.5",

        // Large: 48px height - for hero CTAs and prominent actions
        lg: "h-12 px-8 py-3",

        // Small: 36px height - for inline actions, tertiary buttons
        sm: "h-9 px-4 py-1.5 text-sm font-medium",

        // Extra Small: 32px height - for compact table actions (desktop only)
        // Note: Use sm size on mobile viewports to maintain 44px touch targets
        xs: "h-8 px-3 py-1 text-xs font-medium",

        // Icon sizes: Square buttons for icon-only use
        icon: "size-11",
        "icon-sm": "size-9",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      // A bare <button> defaults to type="submit", so any Button dropped inside a
      // <form> submits it. Default to "button"; the {...props} spread below keeps
      // an explicit type="submit" working.
      type={asChild ? undefined : "button"}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
